// /signup/checkout — embedded Stripe Checkout for a brand-new medical_practice
// org. The org is NOT created until checkout succeeds; on completion Stripe
// redirects to /signup/complete which materializes the org.
//
// Inputs: location.state.orgForm — { name, address_line1, city, state, zip,
// phone } from OrgSignupPage step 3. If absent (e.g. direct visit / refresh),
// we send the user back to /register.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { CreditCard, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import { formatPricePerLocation } from '../../lib/pipStates';
import { TRIAL_DAYS } from '../../lib/trial';
import TrialTimeline from './TrialTimeline';
import { getReferral } from '../../lib/referral';
import PageLoader from '../shared/PageLoader';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function NewOrgCheckoutPage() {
  const { session, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Refresh-proof: navigation state dies on reload, and bouncing a fully
  // verified signup back to /register at the payment step is how prospects
  // get lost. Persist the form per-tab and fall back to it.
  const orgForm = React.useMemo(() => {
    const fromNav = location.state?.orgForm || null;
    if (fromNav) {
      try { sessionStorage.setItem('ml-signup-orgform', JSON.stringify(fromNav)); } catch { /* storage blocked */ }
      return fromNav;
    }
    try { return JSON.parse(sessionStorage.getItem('ml-signup-orgform')) || null; } catch { return null; }
  }, [location.state]);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Provider-type + state pricing — same logic the signup review used.
  const perLocationPrice = formatPricePerLocation(orgForm?.state, orgForm?.provider_type);

  useEffect(() => {
    if (!orgForm || !session) return;
    let cancelled = false;
    setLoading(true); setError(null);
    api.createNewOrgCheckoutSession({
      org_type: 'medical_practice',
      name: orgForm.name,
      address_line1: orgForm.address_line1,
      city: orgForm.city,
      state: orgForm.state,
      zip: orgForm.zip,
      phone: orgForm.phone,
      // Server re-verifies this against NPPES before creating the
      // Stripe session — see server/src/routes/signupCheckout.js.
      npi: orgForm.npi,
      // Drives per-location pricing; server validates against the
      // canonical provider-type list.
      provider_type: orgForm.provider_type,
      // Referral attribution (first-touch, from localStorage) — rides the
      // Stripe session metadata onto the org row at creation.
      referral_source: getReferral()?.code || null,
      referral_meta: getReferral() || null,
      // BAA version the user affirmatively accepted on the review step;
      // rides the Stripe session metadata so checkoutCompletion can record
      // which document text they agreed to.
      baa_version: orgForm.baa_version,
    })
      .then(({ client_secret }) => { if (!cancelled) setClientSecret(client_secret); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgForm, session]);

  if (authLoading) return <PageLoader />;
  if (!session)    return <Navigate to="/login" replace />;
  // Direct visit without form data — restart the flow.
  if (!orgForm)    return <Navigate to="/register" replace />;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #efeef8 0%, #ecebf6 50%, #ecebf6 100%)',
        padding: '56px 24px 96px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#0da7ca', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 16 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0da7ca' }} />
            Activate {orgForm.name}
          </div>
          <h1 style={{
            fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: 'clamp(32px, 4vw, 44px)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: '0 0 16px',
            color: '#0b0b14',
          }}>
            One step from <em style={{ color: '#0da7ca', fontWeight: 400 }}>going live</em>.
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(11,11,20,0.55)', margin: 0, maxWidth: 480, marginInline: 'auto' }}>
            {`Your account is created and your details are saved. Stripe confirms your ${TRIAL_DAYS} free days below — there's no card field, and nothing is charged today.`}
          </p>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid rgba(11,11,20,0.08)',
          borderRadius: 16,
          padding: '20px 24px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 8px 24px -8px rgba(11,11,20,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: 'rgba(13,167,202,0.10)', color: '#0da7ca',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CreditCard size={18} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0b0b14' }}>MediLink Pro</div>
              <div style={{ fontSize: 12, color: 'rgba(11,11,20,0.55)', marginTop: 2 }}>
                {TRIAL_DAYS} days free · no card to start · cancel anytime
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <span style={{
              fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: 28, fontWeight: 700, color: '#0b0b14',
            }}>{perLocationPrice}</span>
            <span style={{ fontSize: 12, color: 'rgba(11,11,20,0.55)', marginLeft: 4 }}>/location/mo after your free days</span>
          </div>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid rgba(11,11,20,0.08)',
          borderRadius: 16,
          minHeight: 540,
          overflow: 'hidden',
          boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 24px 48px -16px rgba(11,11,20,0.10)',
        }}>
          {loading && (
            <div style={{ minHeight: 540, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PageLoader variant="block" sub="Stripe" label="Loading secure checkout…" />
            </div>
          )}
          {error && (
            <div style={{ padding: 24, color: '#b91c1c', fontSize: 13 }}>
              Couldn't load checkout: {error}
              <div style={{ marginTop: 12 }}>
                <button
                  onClick={() => navigate('/register', { state: { resume: orgForm } })}
                  style={{ background: 'none', border: 'none', color: '#0da7ca', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                >
                  ← Back to practice details
                </button>
              </div>
            </div>
          )}
          {clientSecret && (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 24, color: 'rgba(11,11,20,0.55)', fontSize: 12 }}>
          <ShieldCheck size={14} />
          {`No card needed to start. Add one any time before your ${TRIAL_DAYS} free days end — if you don't, your account simply pauses. Nothing is charged.`}
        </div>
      </div>
    </div>
  );
}
